/**
 * NorthPointPicker Component
 * 
 * Allows users to select a north point symbol style and configure its placement.
 */

import React, { useState } from 'react';
import { Compass, RotateCw, Check, X, ArrowUpLeft, ArrowUpRight, ArrowDownLeft, ArrowDownRight } from 'lucide-react';
import { 
  NORTH_POINT_SYMBOLS, 
  NorthPointStyle, 
  northPointToSvg,
  DEFAULT_NORTH_POINT_SIZE 
} from '../constants/northPointSymbols';
import { NorthPoint } from '../types';

interface NorthPointPickerProps {
  /** Currently selected north point (null if none) */
  currentNorthPoint: NorthPoint | null;
  /** Callback when north point is added or updated */
  onNorthPointChange: (northPoint: NorthPoint | null) => void;
  /** Whether the picker is open */
  isOpen: boolean;
  /** Callback to close the picker */
  onClose: () => void;
  /** Optional className */
  className?: string;
}

const NorthPointPicker: React.FC<NorthPointPickerProps> = ({
  currentNorthPoint,
  onNorthPointChange,
  isOpen,
  onClose,
  className = ''
}) => {
  const [selectedStyle, setSelectedStyle] = useState<NorthPointStyle>(
    currentNorthPoint?.style || 'n-arrow'
  );
  const [rotation, setRotation] = useState(currentNorthPoint?.rotation || 0);
  const [scale, setScale] = useState(currentNorthPoint?.scale || 1);
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: currentNorthPoint?.x || 0.9,
    y: currentNorthPoint?.y || 0.1
  });
  
  // Position presets (normalized 0-1)
  const positionPresets = [
    { id: 'top-left', label: 'Top Left', x: 0.1, y: 0.1, icon: ArrowUpLeft },
    { id: 'top-right', label: 'Top Right', x: 0.9, y: 0.1, icon: ArrowUpRight },
    { id: 'bottom-left', label: 'Bottom Left', x: 0.1, y: 0.9, icon: ArrowDownLeft },
    { id: 'bottom-right', label: 'Bottom Right', x: 0.9, y: 0.9, icon: ArrowDownRight },
  ];

  if (!isOpen) return null;

  const handleApply = () => {
    const northPoint: NorthPoint = {
      id: currentNorthPoint?.id || `np-${Date.now()}`,
      style: selectedStyle,
      x: position.x,
      y: position.y,
      rotation,
      scale
    };
    onNorthPointChange(northPoint);
    onClose();
  };

  const handleRemove = () => {
    onNorthPointChange(null);
    onClose();
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm ${className}`}>
      <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-900/50 rounded-lg">
              <Compass size={20} className="text-teal-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white">North Point</h3>
              <p className="text-xs text-slate-400">Select symbol style</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Symbol Grid */}
        <div className="p-6">
          <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3 block">
            Symbol Style
          </label>
          <div className="grid grid-cols-3 gap-3">
            {NORTH_POINT_SYMBOLS.map((symbol) => {
              const isSelected = selectedStyle === symbol.id;
              const svgString = northPointToSvg(symbol, isSelected ? '#14b8a6' : '#94a3b8', 48);
              
              return (
                <button
                  key={symbol.id}
                  onClick={() => setSelectedStyle(symbol.id)}
                  className={`
                    relative p-4 rounded-xl border-2 transition-all
                    flex flex-col items-center gap-2
                    ${isSelected 
                      ? 'border-teal-500 bg-teal-900/30' 
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600 hover:bg-slate-800'
                    }
                  `}
                >
                  <div 
                    dangerouslySetInnerHTML={{ __html: svgString }}
                    style={{ transform: `rotate(${rotation}deg)` }}
                  />
                  <span className={`text-xs ${isSelected ? 'text-teal-300' : 'text-slate-400'}`}>
                    {symbol.name.split(' ')[0]}
                  </span>
                  {isSelected && (
                    <div className="absolute top-2 right-2 p-0.5 bg-teal-500 rounded-full">
                      <Check size={10} className="text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Controls */}
        <div className="px-6 pb-4 space-y-4">
          {/* Position Presets */}
          <div>
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2 block">
              Position
            </label>
            <div className="grid grid-cols-4 gap-2">
              {positionPresets.map((preset) => {
                const Icon = preset.icon;
                const isSelected = position.x === preset.x && position.y === preset.y;
                return (
                  <button
                    key={preset.id}
                    onClick={() => setPosition({ x: preset.x, y: preset.y })}
                    className={`
                      flex flex-col items-center gap-1 p-2 rounded-lg border transition-all text-xs
                      ${isSelected 
                        ? 'border-teal-500 bg-teal-900/30 text-teal-300' 
                        : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600 hover:text-slate-300'
                      }
                    `}
                    title={preset.label}
                  >
                    <Icon size={16} />
                    <span className="hidden sm:inline">{preset.label.split(' ')[0]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rotation */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                Rotation
              </label>
              <span className="text-sm text-slate-300">{rotation}°</span>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="360"
                step="15"
                value={rotation}
                onChange={(e) => setRotation(Number(e.target.value))}
                className="flex-1 h-2 bg-slate-700 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 
                  [&::-webkit-slider-thumb]:bg-teal-500 [&::-webkit-slider-thumb]:rounded-full 
                  [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
              />
              <button
                onClick={() => setRotation((rotation + 45) % 360)}
                className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 transition-colors"
                title="Rotate 45°"
              >
                <RotateCw size={16} />
              </button>
            </div>
          </div>

          {/* Size */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                Size
              </label>
              <span className="text-sm text-slate-300">{Math.round(scale * 100)}%</span>
            </div>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={scale}
              onChange={(e) => setScale(Number(e.target.value))}
              className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 
                [&::-webkit-slider-thumb]:bg-teal-500 [&::-webkit-slider-thumb]:rounded-full 
                [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700 bg-slate-800/30">
          {currentNorthPoint ? (
            <button
              onClick={handleRemove}
              className="px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
            >
              Remove
            </button>
          ) : (
            <div />
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-500 rounded-lg transition-colors"
            >
              {currentNorthPoint ? 'Update' : 'Add North Point'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NorthPointPicker;

/**
 * NorthPointButton Component
 * 
 * Toolbar button to open the north point picker
 */
interface NorthPointButtonProps {
  hasNorthPoint: boolean;
  onClick: () => void;
  disabled?: boolean;
}

export const NorthPointButton: React.FC<NorthPointButtonProps> = ({
  hasNorthPoint,
  onClick,
  disabled = false
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all
        ${hasNorthPoint 
          ? 'bg-teal-900/50 text-teal-300 border border-teal-500/50 hover:bg-teal-900' 
          : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 hover:text-white'
        }
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
      title="Add North Point"
    >
      <Compass size={16} />
      <span className="hidden sm:inline">North</span>
      {hasNorthPoint && (
        <Check size={14} className="text-teal-400" />
      )}
    </button>
  );
};
